import {
    DynamicListAddButton,
    DynamicListWrapperProps,
    ValidatedDynamicList,
    ValidatedDynamicListFormComponentProps
} from '@finbeat/react-toolkit';
import { BOValidatedDatePicker } from 'base-components/BOValidatedDatePicker/BOValidatedDatePicker';
import { dateFormat } from 'constans/dateFormat';
import { format } from 'date-fns';
import {
    ScheduleItem,
    SettingPeriod,
    UnloadSetting,
    UnloadSettingCreate
} from 'models/api/fundsTransfer';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Col, Row, Table } from 'reactstrap';
import ModalFundsTransferSettingTime from './ModalFundsTransferSettingTime';

type PeriodProps = ValidatedDynamicListFormComponentProps & {
    disabled: boolean;
};

const ModalFundsTransferSettingPeriod = ({ disabled }: { disabled: boolean }) => {
    const { t } = useTranslation();
    const FormComponent = useCallback(
        (props: ValidatedDynamicListFormComponentProps) => (
            <Period {...props} disabled={disabled} />
        ),
        [disabled]
    );
    const PeriodWrapper = useCallback(
        ({ children, ...props }: DynamicListWrapperProps) =>
            WrapperSetting({ children, title: t('BackOfficePeriods'), ...props }),
        [disabled]
    );
    return (
        <Row>
            <Col>
                <ValidatedDynamicList<UnloadSettingCreate, SettingPeriod>
                    wrapper={PeriodWrapper}
                    disabled={disabled}
                    emptyRecord={{
                        ed: format(new Date(), dateFormat),
                        sd: format(new Date(), dateFormat),
                        schedule: [],
                    }}
                    name="periods"
                    formComponent={FormComponent}
                    compareFunc={(a, b) => a?.sd === b?.sd && a?.ed === b?.ed}
                />
            </Col>
        </Row>
    );
};

const Period = ({ idx, fieldId, checkDouble, disabled }: PeriodProps) => {
    const { t } = useTranslation();
    const { timeFormat } = useGeneralSettingsContext();
    const FormComponent = useCallback(
        (props: ValidatedDynamicListFormComponentProps) =>
            ModalFundsTransferSettingTime({ ...props, periodIdx: idx, disabled }),
        [idx, fieldId, disabled]
    );
    const TimeWrapper = useCallback(
        ({ children, ...props }: DynamicListWrapperProps) =>
            WrapperSetting({ children, title: t('BackOfficeUnloadSchedule'), ...props }),
        [disabled]
    );
    const { watch } = useFormContext<UnloadSetting>();
    const CurrentPeriod = watch(`periods.${idx}`);

    const validator = (value?: string) => {
        const double = checkDouble(fieldId);

        if (double) {
            return double;
        }

        if (CurrentPeriod.sd > CurrentPeriod.ed)
            return t('BackOfficeUnloadSettingPeriodErrorSubsequence');
    };

    return (
        <td>
            <Row>
                <Col>
                    <BOValidatedDatePicker
                        disabled={disabled}
                        inputProps={{
                            label: t('BackOfficeDtFrom'),
                        }}
                        name={`periods.${idx}.sd`}
                        dateFormat={dateFormat}
                        dateOnly
                        rules={{
                            validate: validator,
                        }}
                    />
                </Col>
                <Col>
                    <BOValidatedDatePicker
                        disabled={disabled}
                        inputProps={{
                            label: t('BackOfficeTo'),
                        }}
                        name={`periods.${idx}.ed`}
                        dateFormat={dateFormat}
                        dateOnly
                        rules={{
                            validate: () => checkDouble(fieldId) ?? undefined,
                        }}
                    />
                </Col>
            </Row>
            <Row>
                <ValidatedDynamicList<UnloadSetting, ScheduleItem>
                    wrapper={TimeWrapper}
                    disabled={disabled}
                    emptyRecord={{
                        time: format(new Date(), timeFormat + ':00'),
                    }}
                    name={`periods.${idx}.schedule`}
                    formComponent={FormComponent}
                    compareFunc={(a, b) => a?.time === b?.time}
                />
            </Row>
        </td>
    );
};

const WrapperSetting = ({
    children,
    title,
    ...props
}: {
    title: string;
} & DynamicListWrapperProps) => {
    return (
        <Table bordered>
            <thead>
                <tr className="align-middle">
                    <th>
                        <Trans i18nKey={title} />
                    </th>
                    <th className="w-10 text-center">
                        <DynamicListAddButton {...props} />
                    </th>
                </tr>
            </thead>
            <tbody>{children}</tbody>
        </Table>
    );
};

export default ModalFundsTransferSettingPeriod;
